/**
 * STAFF & ACCESS — Phibian's own Console staff (owner directive 2026-10-08,
 * dual onboarding part A). Entirely separate from Customers & Onboarding: these
 * are people who operate console.phibian.io, never customer users.
 *
 * Every action here is authorized server-side by `platform:staff.administer`
 * and re-checked in the database (no escalation, no self-change, never zero
 * administrators). This screen renders what the server returns and the
 * refusals it gives; it decides nothing. Every change that ends someone's
 * access or sessions is confirmed in a dialog that states the consequence.
 *
 * The one-time invitation link is shown once, in this tab, and is never sent
 * to analytics or a log.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import { CONSOLE_AREAS } from '../platform/authority.js';
import { isStaffToken, staffRequests } from '../platform/staffSession.js';
import type {
  InvitationCreated,
  StaffDirectory,
  StaffInvitation,
  StaffMember,
} from '../platform/staffSession.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import type { PendingConfirmation } from './ConfirmDialog.js';

type Props = { actor: PlatformActor; client: ConsoleClient };
type Act = (request: Parameters<ConsoleClient>[0], success: string) => Promise<void>;

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const MEMBER_TONE: Record<StaffMember['status'], string> = {
  active: 'good',
  suspended: 'look',
  deactivated: 'plain',
};
const INVITATION_TONE: Record<StaffInvitation['status'], string> = {
  pending: 'sky',
  accepted: 'good',
  expired: 'look',
  revoked: 'plain',
};

export function StaffAccess({ actor, client }: Props) {
  if (!isStaffToken(actor.systemToken)) {
    return (
      <section>
        <h2>Signed in with an operator credential</h2>
        <p>
          <strong>{actor.operatorId}</strong> holds these platform authorities:
        </p>
        <ul>
          {CONSOLE_AREAS.map((a) => (
            <li key={a.id}>
              <code>{a.capability}</code> — granted by <code>{a.credentialEnvVar}</code>
            </li>
          ))}
        </ul>
        <div className="banner">
          <strong>Staff administration needs an individual sign-in.</strong> No operator credential
          can invite or manage staff, by design. Use &ldquo;Sign in as yourself&rdquo; with two-step
          verification once this environment enables it.
        </div>
      </section>
    );
  }
  return (
    <>
      <StaffAdministration actor={actor} client={client} />
      <StaffActivity actor={actor} client={client} />
    </>
  );
}

const ACTION_TEXT: Record<string, string> = {
  bootstrap: 'Owner bootstrap',
  invited: 'Invited',
  invitation_resent: 'Invitation replaced',
  invitation_revoked: 'Invitation revoked',
  invitation_accepted: 'Invitation accepted',
  role_changed: 'Role changed',
  suspended: 'Suspended',
  reactivated: 'Reactivated',
  deactivated: 'Deactivated',
  sessions_revoked: 'Sessions ended',
  factor_repin_issued: 'Authenticator re-pin issued',
  factor_repinned: 'New authenticator pinned',
  invitation_address_reset: 'Invited address reset',
};

/** Staff activity audit (administrators only): administration and sign-in events. */
function StaffActivity({ actor, client }: Props) {
  const [events, setEvents] = useState<ConsoleOutcome<{ events: StaffActivityEvent[] }> | null>(
    null,
  );
  useEffect(() => {
    void client<{ events: StaffActivityEvent[] }>(staffRequests.activity(), actor).then(setEvents);
  }, [actor, client]);
  if (events === null || events.kind !== 'ok') return null;
  return (
    <section>
      <h3>Recent staff activity</h3>
      <p className="quiet">
        The newest {events.value.events.length} administration and sign-in events, from the
        insert-only staff logs. No codes, tokens or passwords are ever recorded.
      </p>
      <table className="stack">
        <thead>
          <tr>
            <th>When</th>
            <th>What</th>
            <th>By</th>
            <th>Who</th>
          </tr>
        </thead>
        <tbody>
          {events.value.events.map((e, i) => (
            <tr key={`${e.at}-${i}`}>
              <td data-label="When">{new Date(e.at).toLocaleString()}</td>
              <td data-label="What">
                {e.kind === 'sign_in' ? (
                  <span
                    className="pill"
                    data-tone={
                      e.action.startsWith('signed_in')
                        ? 'good'
                        : e.action.startsWith('refused')
                          ? 'look'
                          : 'sky'
                    }
                  >
                    {e.action.replace(':', ' · ').replace(/_/g, ' ')}
                  </span>
                ) : (
                  (ACTION_TEXT[e.action] ?? e.action.replace(/_/g, ' '))
                )}
              </td>
              <td data-label="By">{e.actorEmail ?? (e.kind === 'change' ? 'maintenance' : '—')}</td>
              <td data-label="Who">{e.subjectEmail ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

interface StaffActivityEvent {
  readonly at: string;
  readonly kind: 'change' | 'sign_in';
  readonly action: string;
  readonly actorEmail: string | null;
  readonly subjectEmail: string | null;
}

function StaffAdministration({ actor, client }: Props) {
  const [directory, setDirectory] = useState<ConsoleOutcome<StaffDirectory> | null>(null);
  const [invitations, setInvitations] = useState<ConsoleOutcome<{
    invitations: StaffInvitation[];
  }> | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'problem'; text: string } | null>(null);
  const [created, setCreated] = useState<InvitationCreated | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [confirming, setConfirming] = useState<PendingConfirmation | null>(null);

  const load = useCallback(async () => {
    const [d, i] = await Promise.all([
      client<StaffDirectory>(staffRequests.directory(), actor),
      client<{ invitations: StaffInvitation[] }>(staffRequests.invitations(), actor),
    ]);
    setDirectory(d);
    setInvitations(i);
  }, [actor, client]);

  useEffect(() => {
    void load();
  }, [load]);

  const act: Act = async (request, success) => {
    const r = await client<unknown>(request, actor);
    setNotice(
      r.kind === 'ok' ? { kind: 'ok', text: success } : { kind: 'problem', text: r.message },
    );
    await load();
  };

  if (directory === null) return <p className="quiet">Reading the staff directory…</p>;
  if (directory.kind !== 'ok') {
    return (
      <div className="banner" role="alert">
        <strong>Staff administration is not available to you.</strong> {directory.message}
      </div>
    );
  }
  const roles = directory.value.roles;
  const staff = directory.value.staff;
  const pending =
    invitations?.kind === 'ok'
      ? invitations.value.invitations.filter((i) => i.status === 'pending').length
      : null;
  const link = created ? `${window.location.origin}${created.acceptPath}` : '';

  return (
    <>
      <ConfirmDialog pending={confirming} onClose={() => setConfirming(null)} />

      <div className="metrics">
        <Metric label="Active staff" value={staff.filter((m) => m.status === 'active').length} />
        <Metric
          label="Suspended or deactivated"
          value={staff.filter((m) => m.status !== 'active').length}
        />
        <Metric label="Pending invitations" value={pending ?? '—'} />
        <Metric
          label="Live sessions"
          value={staff.reduce((n, m) => n + m.activeSessions, 0)}
          note="Across all staff, now"
        />
      </div>

      {notice ? (
        <div className="banner" role={notice.kind === 'problem' ? 'alert' : 'status'}>
          {notice.text}
        </div>
      ) : null}

      <section aria-labelledby="invite-heading">
        <h2 id="invite-heading">Invite a staff member</h2>
        <p className="quiet">
          Staff can only receive roles whose capabilities you hold. The invitation lasts 72 hours
          and can be used once. No email is sent: you share the link yourself.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void (async () => {
              const r = await client<InvitationCreated>(
                staffRequests.invite(email.trim(), role),
                actor,
              );
              if (r.kind === 'ok') {
                setCreated(r.value);
                setEmail('');
                setNotice(null);
              } else {
                setCreated(null);
                setNotice({ kind: 'problem', text: r.message });
              }
              await load();
            })();
          }}
        >
          <div className="row">
            <div className="col">
              <label htmlFor="inviteEmail">Work email</label>
              <input
                id="inviteEmail"
                type="email"
                autoComplete="off"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="col">
              <label htmlFor="inviteRole">Role</label>
              <select
                id="inviteRole"
                required
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">Choose a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {role ? (
            <p className="quiet">
              {roles.find((r) => r.id === role)?.capabilities.length
                ? `Grants: ${roles.find((r) => r.id === role)!.capabilities.join(', ')}`
                : 'Grants no platform capability yet (read-only roles wait on a read-only capability).'}
            </p>
          ) : null}
          <p>
            <button type="submit" disabled={!email || !role}>
              Create invitation
            </button>
          </p>
        </form>
        {created ? (
          <div className="card" role="status" style={{ marginTop: 16 }}>
            <strong>
              {created.repin
                ? 'Authenticator re-pin link'
                : created.resent
                  ? 'Invitation replaced'
                  : 'Invitation created'}
            </strong>{' '}
            — copy this link now. It is shown only once and expires {when(created.expiresAt)}.
            <pre aria-label="One-time invitation link">{link}</pre>
            <div className="actions">
              <button
                type="button"
                className="primary small"
                onClick={() => void navigator.clipboard?.writeText(link)}
              >
                Copy link
              </button>
              <button type="button" className="small" onClick={() => setCreated(null)}>
                Done — hide it
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section aria-labelledby="staff-heading">
        <h2 id="staff-heading">Staff</h2>
        <table className="stack">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Status</th>
              <th>Two-step</th>
              <th>Last sign-in</th>
              <th>Sessions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                self={m.email === actor.operatorId}
                roles={roles}
                act={act}
                confirm={setConfirming}
                repin={async () => {
                  const r = await client<InvitationCreated>(staffRequests.repin(m.id), actor);
                  if (r.kind === 'ok') {
                    setCreated({ ...r.value, resent: false, repin: true });
                    setNotice({
                      kind: r.value.identityPlatformFactorsCleared === false ? 'problem' : 'ok',
                      text:
                        r.value.identityPlatformFactorsCleared === false
                          ? `Re-pin link created for ${m.email}, but the old authenticator could not be removed at Identity Platform. Follow the staff MFA runbook before sending it.`
                          : `Re-pin link created for ${m.email}. Their old authenticator and sessions no longer work.`,
                    });
                  } else {
                    setNotice({ kind: 'problem', text: r.message });
                  }
                  await load();
                }}
              />
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="invitations-heading">
        <h2 id="invitations-heading">Invitations</h2>
        {invitations?.kind !== 'ok' ? (
          <p className="quiet">{invitations ? invitations.message : 'Reading…'}</p>
        ) : invitations.value.invitations.length === 0 ? (
          <p className="quiet">No invitations yet.</p>
        ) : (
          <table className="stack">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Invited by</th>
                <th>
                  <span className="quiet">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invitations.value.invitations.map((i) => (
                <tr key={i.id}>
                  <td data-label="Email">{i.email}</td>
                  <td data-label="Role">{i.role.replace(/_/g, ' ')}</td>
                  <td data-label="Status">
                    <span className="pill" data-tone={INVITATION_TONE[i.status]}>
                      {i.status}
                    </span>
                  </td>
                  <td data-label="Expires">{when(i.expiresAt)}</td>
                  <td data-label="Invited by">
                    {i.kind === 'bootstrap' ? 'Owner bootstrap' : (i.invitedBy ?? '—')}
                  </td>
                  <td data-label="Actions">
                    {i.status === 'pending' && i.kind === 'invitation' ? (
                      <button
                        type="button"
                        className="small danger"
                        onClick={() =>
                          setConfirming({
                            title: `Revoke the invitation for ${i.email}?`,
                            body: 'The link stops working immediately. You can invite them again later.',
                            confirmLabel: 'Revoke invitation',
                            danger: true,
                            onConfirm: () =>
                              void act(
                                staffRequests.revokeInvitation(i.id),
                                `Revoked the invitation for ${i.email}.`,
                              ),
                          })
                        }
                      >
                        Revoke
                      </button>
                    ) : null}
                    {i.status === 'pending' && i.kind === 'invitation' ? (
                      <button
                        type="button"
                        className="small"
                        onClick={() =>
                          setConfirming({
                            title: `Reset the account at ${i.email}?`,
                            body:
                              'Use this when someone else registered this address first and the invitee is blocked. ' +
                              'The account at this address loses its authenticator, and a password reset is mailed to the address. ' +
                              'Only the mailbox owner can use it. Available with the separate staff sign-in pool only.',
                            confirmLabel: 'Reset account',
                            danger: true,
                            onConfirm: () =>
                              void act(
                                staffRequests.resetInvitationAddress(i.id),
                                `Reset the account at ${i.email}; a password reset was mailed to the address.`,
                              ),
                          })
                        }
                      >
                        Reset address
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function Metric({ label, value, note }: { label: string; value: number | string; note?: string }) {
  return (
    <div className="metric">
      <span className="metric-label">{label}</span>
      <span className="metric-value num">{value}</span>
      {note ? <span className="metric-note">{note}</span> : null}
    </div>
  );
}

function MemberRow({
  member,
  self,
  roles,
  act,
  confirm,
  repin,
}: {
  member: StaffMember;
  self: boolean;
  roles: StaffDirectory['roles'];
  act: Act;
  confirm: (c: PendingConfirmation) => void;
  repin: () => Promise<void>;
}) {
  const [role, setRole] = useState(member.role);
  const roleLabel = (id: string) => roles.find((r) => r.id === id)?.label ?? id.replace(/_/g, ' ');
  return (
    <tr>
      <td data-label="Person">
        <strong>{member.displayName}</strong>
        {self ? <span className="quiet"> (you)</span> : null}
        <br />
        <span className="quiet">{member.email}</span>
      </td>
      <td data-label="Role">
        {self || member.status === 'deactivated' ? (
          roleLabel(member.role)
        ) : (
          <div className="actions">
            <select
              aria-label={`Role for ${member.email}`}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{ width: 'auto', minHeight: 32 }}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
              {roles.some((r) => r.id === member.role) ? null : (
                <option value={member.role}>{member.role}</option>
              )}
            </select>
            {role !== member.role ? (
              <button
                type="button"
                className="small primary"
                onClick={() =>
                  confirm({
                    title: `Change ${member.displayName} to ${roleLabel(role)}?`,
                    body: 'Their capabilities change and every session they have open ends now. They sign in again to continue.',
                    confirmLabel: 'Change role',
                    onConfirm: () =>
                      void act(
                        staffRequests.changeMember(member.id, { role }),
                        `${member.email} is now ${roleLabel(role)}; their sessions were ended.`,
                      ),
                  })
                }
              >
                Apply
              </button>
            ) : null}
          </div>
        )}
      </td>
      <td data-label="Status">
        <span className="pill" data-tone={MEMBER_TONE[member.status]}>
          {member.status}
        </span>
      </td>
      <td data-label="Two-step">
        {/* A staff row exists only after an authenticator was verified at
            acceptance, and sign-in refuses any account without one. */}
        <span className="pill" data-tone="good">
          Authenticator
        </span>
      </td>
      <td data-label="Last sign-in">{when(member.lastSignedInAt)}</td>
      <td data-label="Sessions" className="num">
        {member.activeSessions}
      </td>
      <td data-label="Actions">
        {self ? (
          <span className="quiet">Another administrator changes your access</span>
        ) : (
          <div className="actions">
            {member.status === 'active' ? (
              <button
                type="button"
                className="small"
                onClick={() =>
                  confirm({
                    title: `Suspend ${member.displayName}?`,
                    body: 'They lose access immediately and every open session ends. You can reactivate them later.',
                    confirmLabel: 'Suspend',
                    danger: true,
                    onConfirm: () =>
                      void act(
                        staffRequests.changeMember(member.id, { status: 'suspended' }),
                        `Suspended ${member.email}; their sessions were ended.`,
                      ),
                  })
                }
              >
                Suspend
              </button>
            ) : member.status === 'suspended' ? (
              <button
                type="button"
                className="small"
                onClick={() =>
                  void act(
                    staffRequests.changeMember(member.id, { status: 'active' }),
                    `Reactivated ${member.email}. They must sign in again.`,
                  )
                }
              >
                Reactivate
              </button>
            ) : null}
            {member.activeSessions > 0 ? (
              <button
                type="button"
                className="small"
                onClick={() =>
                  confirm({
                    title: `End ${member.displayName}'s sessions?`,
                    body: `Their ${member.activeSessions} open session(s) end now. Their access is unchanged; they can sign in again.`,
                    confirmLabel: 'End sessions',
                    onConfirm: () =>
                      void act(
                        staffRequests.revokeSessions(member.id),
                        `Ended ${member.email}'s sessions.`,
                      ),
                  })
                }
              >
                End sessions
              </button>
            ) : null}
            {member.status === 'active' && !self ? (
              <button
                type="button"
                className="small"
                onClick={() =>
                  confirm({
                    title: `Re-pin ${member.displayName}'s authenticator?`,
                    body: 'Use this when they have lost their authenticator. Their current authenticator stops working at once, their sessions end, and you get a one-time link (72 hours) for them to add a new one. Their role and access are unchanged. Confirm who they are by a channel you trust before sending the link.',
                    confirmLabel: 'Create re-pin link',
                    danger: true,
                    onConfirm: () => void repin(),
                  })
                }
              >
                Re-pin authenticator
              </button>
            ) : null}
            {member.status !== 'deactivated' ? (
              <button
                type="button"
                className="small danger"
                onClick={() =>
                  confirm({
                    title: `Deactivate ${member.displayName}?`,
                    body: 'Their Console access ends and every open session closes. Deactivation is meant to be permanent; use Suspend for a temporary stop.',
                    confirmLabel: 'Deactivate',
                    danger: true,
                    onConfirm: () =>
                      void act(
                        staffRequests.changeMember(member.id, { status: 'deactivated' }),
                        `Deactivated ${member.email}.`,
                      ),
                  })
                }
              >
                Deactivate
              </button>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}
